"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const encoding_1 = require("@iov/encoding");
const readonly_date_1 = require("readonly-date");
var Algorithm;
(function (Algorithm) {
    Algorithm["Ed25519"] = "ed25519";
    Algorithm["Secp256k1"] = "secp256k1";
})(Algorithm = exports.Algorithm || (exports.Algorithm = {}));
function isPubkeyBundle(data) {
    return (encoding_1.isNonNullObject(data) &&
        (data.algo === Algorithm.Ed25519 ||
            data.algo === Algorithm.Secp256k1) &&
        encoding_1.isUint8Array(data.data));
}
exports.isPubkeyBundle = isPubkeyBundle;
/**
 * Compares two objects that conform to the PubkeyBundle interface for equality.
 *
 * This can also be used to compare pairs of derived types in which case all
 * non-PubkeyBundle fields are ignored.
 *
 * @param left the left hand side of the comparison
 * @param right the right hand side of the comparison
 */
function pubkeyBundleEquals(left, right) {
    return (left.algo === right.algo &&
        left.data.length === right.data.length &&
        left.data.every((value, index) => value === right.data[index]));
}
exports.pubkeyBundleEquals = pubkeyBundleEquals;
function isIdentity(data) {
    return (encoding_1.isNonNullObject(data) &&
        typeof data.chainId === "string" &&
        isPubkeyBundle(data.pubkey));
}
exports.isIdentity = isIdentity;
/**
 * Compares two objects that conform to the Identity interface for equality.
 * All additional (non-Identity) fields are ignored.
 *
 * @param left the left hand side of the comparison
 * @param right the right hand side of the comparison
 */
function identityEquals(left, right) {
    return left.chainId === right.chainId && pubkeyBundleEquals(left.pubkey, right.pubkey);
}
exports.identityEquals = identityEquals;
function swapIdEquals(left, right) {
    return (left.prefix === right.prefix &&
        left.data.length === right.data.length &&
        left.data.every((value, index) => value === right.data[index]));
}
exports.swapIdEquals = swapIdEquals;
// Specifies which hash function to apply before signing.
// The identity function is indicated using None.
var PrehashType;
(function (PrehashType) {
    PrehashType[PrehashType["None"] = 0] = "None";
    PrehashType[PrehashType["Sha512"] = 1] = "Sha512";
    PrehashType[PrehashType["Sha256"] = 2] = "Sha256";
    PrehashType[PrehashType["Keccak256"] = 3] = "Keccak256";
})(PrehashType = exports.PrehashType || (exports.PrehashType = {}));
function isFullSignature(data) {
    if (!encoding_1.isNonNullObject(data))
        return false;
    const fs = data;
    return typeof fs.nonce === "number" && isPubkeyBundle(fs.pubkey) && encoding_1.isUint8Array(fs.signature);
}
exports.isFullSignature = isFullSignature;
function isAmount(data) {
    return (encoding_1.isNonNullObject(data) &&
        typeof data.quantity === "string" &&
        typeof data.fractionalDigits === "number" &&
        typeof data.tokenTicker === "string");
}
exports.isAmount = isAmount;
function isFee(data) {
    return (encoding_1.isNonNullObject(data) &&
        (isAmount(data.tokens) ||
            (isAmount(data.gasPrice) && typeof data.gasLimit === "string")));
}
exports.isFee = isFee;
function isUnsignedTransaction(data) {
    if (!encoding_1.isNonNullObject(data))
        return false;
    const transaction = data;
    return (typeof transaction.kind === "string" &&
        (transaction.fee === undefined || isFee(transaction.fee)) &&
        typeof transaction.chainId === "string");
}
exports.isUnsignedTransaction = isUnsignedTransaction;
function isNonEmptyArray(data) {
    return data.length >= 1;
}
exports.isNonEmptyArray = isNonEmptyArray;
function newNonEmptyArray(data) {
    const a = [...data];
    if (!isNonEmptyArray(a)) {
        throw new Error("Array is empty");
    }
    return a;
}
exports.newNonEmptyArray = newNonEmptyArray;
function isArrayOfFullSignature(signatures) {
    return Array.isArray(signatures) && signatures.every(isFullSignature);
}
function isSignedTransaction(data) {
    if (!encoding_1.isNonNullObject(data))
        return false;
    const transaction = data.transaction;
    if (!isUnsignedTransaction(transaction))
        return false;
    const signatures = data.signatures;
    if (!isArrayOfFullSignature(signatures))
        return false;
    if (!isNonEmptyArray(signatures))
        return false;
    return true;
}
exports.isSignedTransaction = isSignedTransaction;
function isConfirmedTransaction(transaction) {
    return typeof transaction.transaction !== "undefined";
}
exports.isConfirmedTransaction = isConfirmedTransaction;
function isFailedTransaction(transaction) {
    return !isConfirmedTransaction(transaction);
}
exports.isFailedTransaction = isFailedTransaction;
function isConfirmedAndSignedTransaction(transaction) {
    return isConfirmedTransaction(transaction);
}
exports.isConfirmedAndSignedTransaction = isConfirmedAndSignedTransaction;
function isBlockHeightTimeout(timeout) {
    return typeof timeout.height === "number";
}
exports.isBlockHeightTimeout = isBlockHeightTimeout;
function isTimestampTimeout(timeout) {
    return typeof timeout.timestamp === "number";
}
exports.isTimestampTimeout = isTimestampTimeout;
function createTimestampTimeout(secondsFromNow) {
    return { timestamp: Math.floor(readonly_date_1.ReadonlyDate.now() / 1000) + secondsFromNow };
}
exports.createTimestampTimeout = createTimestampTimeout;
function isSendTransaction(transaction) {
    return transaction.kind === "bcp/send";
}
exports.isSendTransaction = isSendTransaction;
//# sourceMappingURL=transactions.js.map